/** The Skip answer as a picklist plus a note, so the Missed report can group skips by reason. */
import { Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, MetadataEntityFieldEditorType, MetadataEntityFieldType, resolveVisitSkipReason, SectionLayout, SurfaceType, VISIT_SKIP_REASON_LABELS, VisitSkipReason } from '@jms/model';
import { injectDialog } from '@workbuddy/ui/v2/blocks/dialog';
import { defineSurface, defineSurfaceSchema, openFormSurfaceDialog } from '@workbuddy/ui/v2/surface.engine';

export interface VisitSkipReasonForm {
  reasonCode?: VisitSkipReason;
  reason?: string;
}

/** What the Skip command is sent: the code always, the note only when one was written. */
export interface VisitSkipReasonPayload {
  reasonCode: VisitSkipReason;
  reason?: string;
}

export interface VisitSkipReasonPromptRequest {
  title: string;
  description: string;
}

const REASON_OPTIONS = Object.values(VisitSkipReason).map(value => ({ value, label: VISIT_SKIP_REASON_LABELS[value] }));

export function buildVisitSkipReasonSchema(description: string) {

  return defineSurfaceSchema(ActivityEntityName.Visit, SurfaceType.Form)
    .model<VisitSkipReasonForm>()
    .submitLabel('Skip')
    .body(body => body.section('reason', section => section
      .layout(SectionLayout.PropertyList)
      .columns(1)
      .description(description)
      .field({ bind: 'reasonCode', required: true, autofocus: true, metadata: { type: MetadataEntityFieldType.Enum, displayName: 'Reason', options: REASON_OPTIONS }, editorInputs: { placeholder: 'Select a reason' } })
      .field({ bind: 'reason', metadata: { type: MetadataEntityFieldType.String, displayName: 'Details', editor: MetadataEntityFieldEditorType.TextArea }, editorInputs: { rows: 2, placeholder: 'Add details' } })))
    // The server refuses Other without text too; requiring it here names the field instead of a 400.
    .rules(rules => rules
      .rule('other-needs-details', rule => rule
        .when(condition => condition.eq('reasonCode', VisitSkipReason.Other))
        .then(effect => effect.required({ type: 'field', id: 'reason' }))))
    .build();
}

/** Same validation the domain applies, so the dialog and the server can never disagree on an answer. */
export function visitSkipReasonPayload(model: VisitSkipReasonForm): VisitSkipReasonPayload {

  const resolved = resolveVisitSkipReason(model.reasonCode, model.reason);
  if (resolved.error) throw new Error(resolved.error);
  const answer = resolved.answer!;

  return answer.text ? { reasonCode: answer.code, reason: answer.text } : { reasonCode: answer.code };
}

/** Resolves undefined when the person backs out, so nothing is written. */
export function promptVisitSkipReason(injector: Injector, request: VisitSkipReasonPromptRequest): Promise<VisitSkipReasonPayload | undefined> {

  const dialogs = runInInjectionContext(injector, injectDialog);

  return openFormSurfaceDialog<VisitSkipReasonPayload>(dialogs, {
    title: request.title,
    createDefinition: api => defineSurface<VisitSkipReasonForm>({
      schema: buildVisitSkipReasonSchema(request.description),
      initialModel: {},
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        api.close(visitSkipReasonPayload(event.model));
        return event.model;
      },
    }),
  });
}
